/**
 * SB6-ANA-CACHE-03 — user-configurable cache budget (client-side prefs).
 *
 * BOTH cache limits are user-configurable (Christian 2026-06-05):
 *   - `maxBytes`           — max unpinned cache size (default 10 GB).
 *   - `maxUnpinnedDatasets`— max number of unpinned datasets (default 3).
 * Whichever trips first drives an eviction pass.
 *
 * ## Why localStorage, not the Lyse preferences blob
 * The cache itself — and therefore the manifest, pins, and a sensible budget —
 * is **per-browser-profile by design** (story §Out of scope: "pin/manifest is
 * per-browser-profile … cross-device sync explicitly not a goal"). The Lyse
 * preferences blob round-trips to the auth server (BR-097) and its Zod schema
 * `.strip()`s unknown keys, so adding budget fields there would be a wire-shape
 * / contract change — which the story forbids ("NO wire-shape change"). The
 * column-visibility model (DataAcquisitionsListFeature) set the precedent for a
 * browser-scoped, localStorage-persisted preference; the budget follows it.
 *
 * The module exposes a tiny subscribable store so the settings UI and the
 * evictor read one source of truth.
 */

const STORAGE_KEY = "@aeriosiq/lyse/cache/budget/v1";

/** 10 GB default unpinned-cache cap. */
export const DEFAULT_MAX_BYTES = 10 * 1024 * 1024 * 1024;
/** 3-dataset default unpinned-dataset cap. */
export const DEFAULT_MAX_UNPINNED_DATASETS = 3;

export interface StorageBudget {
  /** Max unpinned cache bytes. */
  maxBytes: number;
  /** Max number of unpinned datasets. */
  maxUnpinnedDatasets: number;
}

export const DEFAULT_BUDGET: StorageBudget = {
  maxBytes: DEFAULT_MAX_BYTES,
  maxUnpinnedDatasets: DEFAULT_MAX_UNPINNED_DATASETS,
};

function readPersisted(): StorageBudget {
  if (typeof localStorage === "undefined") return { ...DEFAULT_BUDGET };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_BUDGET };
    const parsed = JSON.parse(raw) as Partial<StorageBudget> | null;
    if (!parsed || typeof parsed !== "object") return { ...DEFAULT_BUDGET };
    const maxBytes =
      typeof parsed.maxBytes === "number" &&
      Number.isFinite(parsed.maxBytes) &&
      parsed.maxBytes > 0
        ? parsed.maxBytes
        : DEFAULT_MAX_BYTES;
    const maxUnpinnedDatasets =
      typeof parsed.maxUnpinnedDatasets === "number" &&
      Number.isFinite(parsed.maxUnpinnedDatasets) &&
      parsed.maxUnpinnedDatasets >= 1
        ? Math.floor(parsed.maxUnpinnedDatasets)
        : DEFAULT_MAX_UNPINNED_DATASETS;
    return { maxBytes, maxUnpinnedDatasets };
  } catch {
    return { ...DEFAULT_BUDGET };
  }
}

class StorageBudgetStore {
  private budget: StorageBudget = readPersisted();
  private listeners = new Set<() => void>();

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  getBudget = (): StorageBudget => this.budget;

  private persist(): void {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.budget));
    } catch {
      /* private mode / quota — in-memory value still drives this session */
    }
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  setMaxBytes(bytes: number): void {
    const next =
      Number.isFinite(bytes) && bytes > 0 ? bytes : DEFAULT_MAX_BYTES;
    if (next === this.budget.maxBytes) return;
    this.budget = { ...this.budget, maxBytes: next };
    this.persist();
    this.emit();
  }

  setMaxUnpinnedDatasets(count: number): void {
    const next =
      Number.isFinite(count) && count >= 1
        ? Math.floor(count)
        : DEFAULT_MAX_UNPINNED_DATASETS;
    if (next === this.budget.maxUnpinnedDatasets) return;
    this.budget = { ...this.budget, maxUnpinnedDatasets: next };
    this.persist();
    this.emit();
  }

  /** @internal — reset to defaults (tests). */
  __reset(): void {
    this.budget = { ...DEFAULT_BUDGET };
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    this.emit();
  }

  /** @internal — re-read from localStorage (tests). */
  __reload(): void {
    this.budget = readPersisted();
    this.emit();
  }
}

export const storageBudget = new StorageBudgetStore();
